// ==================

import React, {
  useState,
  useContext,
  useRef,
  useEffect,
  useCallback,
  ChangeEvent,
  MouseEvent,
  ReactNode,
} from 'react';
import styles from './MultiSelectWithSearch.module.css';
import { useTranslations } from 'next-intl';
import RenderHelpBox from '../RenderHelpBox';
import { useLayoutContext } from '@/context/LayoutContext';

interface TagItem {
  id: string | number;
  title: string;
}

interface ApiCallParams {
  params?: {
    title?: string;
  };
}

interface AddApiCallParams {
  title: string;
}

interface ApiResponse<T> {
  data?: {
    response: T;
  };
}

interface ItemObject {
  key?: string;
}

interface TagInputProps {
  initialSelected?: TagItem[];
  onChange?: (selectedItems: TagItem[], itemKey?: string) => void;
  apiCall: (params: ApiCallParams) => Promise<ApiResponse<TagItem[]>>;
  addApiCall: (params: AddApiCallParams) => Promise<ApiResponse<TagItem>>;
  item?: ItemObject;
  extraInfoStyle?: string;
  extraInfo?: ReactNode;
  guide?: any;
  title?: string;
}

export default function TagInput({
  initialSelected = [],
  onChange,
  apiCall,
  addApiCall,
  item,
  extraInfoStyle,
  extraInfo,
  guide,
  title = '',
}: TagInputProps): JSX.Element {
  const tCommon = useTranslations('common');
  const { showError } = useLayoutContext();

  const [showSearch, setShowSearch] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<TagItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback((event: globalThis.MouseEvent) => {
    if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
      setFocused('');
      setShowSearch(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  useEffect(() => {
    const delay = setTimeout(() => {
      if (query.trim()) {
        setLoading(true);
        apiCall({ params: { title: query } })
          .then((res: ApiResponse<TagItem[]>) => {
            setResults(res?.data?.response || []);
            setLoading(false);
          })
          .catch((err) => {
            setResults([]);
            setLoading(false);
            showError(err);
          });
      } else {
        setResults([]);
      }
    }, 400);

    return () => clearTimeout(delay);
  }, [query, apiCall]);

  const addItem = useCallback(
    (newItem: TagItem) => {
      if (!newItem || newItem.id === undefined || newItem.title === undefined) {
        console.error('Attempted to add invalid item:', newItem);
        return;
      }
      const exists = initialSelected?.some((i) => i.id === newItem.id);
      if (!exists) {
        onChange && onChange([...initialSelected, newItem], item?.key);
      }
      setShowSearch(false);
      setFocused('');
      setQuery('');
      setResults([]);
    },
    [initialSelected, onChange, item?.key],
  );

  const removeItem = useCallback(
    (id: string | number) => {
      onChange &&
        onChange(
          initialSelected?.filter((i) => i.id !== id),
          item?.key,
        );
    },
    [initialSelected, onChange, item?.key],
  );

  const handleKeyDown = useCallback(
    async (e: React.KeyboardEvent<HTMLInputElement>) => {
      const inputElement = e.target as HTMLInputElement;
      const inputText = inputElement.value.trim();

      if (e.key === 'Enter') {
        e.preventDefault();

        if (!inputText) return;

        const isAlreadyInResults = results.some(
          (i) => i.title.toLowerCase() === inputText.toLowerCase(),
        );
        const isAlreadySelected = initialSelected?.some(
          (i) => i.title.toLowerCase() === inputText.toLowerCase(),
        );

        if (!isAlreadyInResults && !isAlreadySelected && addApiCall && !loading) {
          setLoading(true);
          try {
            const res = await addApiCall({ title: inputText });
            const newItem = res?.data?.response;
            if (newItem?.id !== undefined && newItem?.title !== undefined) {
              addItem(newItem);
            }
          } catch (error) {
            console.error('Error adding tag:', error);
          } finally {
            setLoading(false);
          }
        } else if (isAlreadySelected) {
          console.log('Item already selected.');
          setQuery('');
          setShowSearch(false);
          setFocused('');
        } else if (isAlreadyInResults) {
          console.log('Item found in results, click to add.');
        }
      }
    },
    [query, results, initialSelected, addApiCall, addItem, loading, item?.key],
  );

  const handleAddButtonClick = useCallback(() => {
    setShowSearch((prev) => !prev);
    setFocused(focused?.length > 0 ? '' : title);
  }, [focused, title]);

  const renderHelp = title && guide ? <>{RenderHelpBox(title, guide)}</> : null;

  return (
    <div className={styles.container}>
      {title && (
        <div className={`${styles.title} formTitle`}>
          {' '}
          <span>
            {title}
            {extraInfo && <span className={extraInfoStyle}>{extraInfo}</span>}
          </span>
          {renderHelp}
        </div>
      )}
      <div className={`${styles.selectedBox} ${focused === title ? 'formActiveBorder' : ''}`}>
        {' '}
        {initialSelected?.map((i) => (
          <div key={i.id} className={styles.chip}>
            <span>{i.title}</span>
            <button
              className={styles.removeBtn}
              onClick={() => removeItem(i.id)}
              aria-label={`Remove ${i.title}`}
            >
              ✕
            </button>
          </div>
        ))}
        <button
          className={`${styles.addBtn}  secondButton`}
          onClick={handleAddButtonClick}
          aria-label="Add new tag"
        >
          +
        </button>
      </div>

      {/* Search Box */}
      {showSearch && (
        <div className={styles.searchBox} ref={containerRef}>
          <input
            type="text"
            className={styles.input}
            placeholder={tCommon('tagInputPlaceholder')}
            value={query}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            // ref={containerRef}
            aria-label="Search or add tag"
          />
          {loading && <p className={styles.infoText}>{tCommon('loading')} ...</p>}
          {!loading && results.length > 0 && (
            <div className={styles.resultsBox}>
              {results.map((i) => (
                <div
                  key={i.id}
                  className={styles.resultItem}
                  onClick={() => addItem(i)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      addItem(i);
                    }
                  }}
                >
                  {i.title}
                </div>
              ))}
            </div>
          )}
          {!loading && query && results.length === 0 && (
            <p className={styles.infoText}>{tCommon('noResultTagInput')}</p>
          )}
        </div>
      )}
    </div>
  );
}
